import type { ProductDto, UpsertProductInput } from '@telestore/contracts';
import { pickText } from '@telestore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compareSizes } from '@telestore/catalog';
import { parseAmount, toDecimal } from '@telestore/shared';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { ProductVerticalPanel } from './product-vertical.js';
import {
  Button,
  Empty,
  ErrorBox,
  Field,
  Input,
  Money,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
  Textarea,
} from '../components/ui.js';

/**
 * Catalog management.
 *
 * Two things dominate a fashion merchant's day: adding a product with a full
 * size × colour grid, and correcting stock after a sale in the physical shop.
 * Both are one screen away, and stock is editable inline — making them open a
 * product form to change a number from 3 to 2 is how a tool gets abandoned.
 */
export function ProductsPage() {
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ProductDto | 'new' | null>(null);
  const [stockDraft, setStockDraft] = useState<Record<string, number>>({});

  const products = useQuery({
    queryKey: ['products', shopId, search],
    queryFn: () => api.products(shopId!, search ? { q: search } : {}),
    enabled: Boolean(shopId),
  });

  const saveStock = useMutation({
    mutationFn: () =>
      api.adjustStock(
        shopId!,
        Object.entries(stockDraft).map(([variantId, stock]) => ({ variantId, stock })),
      ),
    onSuccess: () => {
      setStockDraft({});
      void queryClient.invalidateQueries({ queryKey: ['products', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', shopId] });
    },
  });

  if (editing) {
    return (
      <ProductForm
        product={editing === 'new' ? null : editing}
        onDone={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['products', shopId] });
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Input
          className="max-w-64"
          placeholder={t('storefront.searchProducts')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Button className="ml-auto" onClick={() => setEditing('new')}>
          + {t('admin.addProduct')}
        </Button>
      </div>

      {Object.keys(stockDraft).length > 0 ? (
        <div
          className="panel flex items-center justify-between p-3"
          style={{ borderColor: 'var(--color-brand)', background: 'var(--color-brand-soft)' }}
        >
          <span className="text-sm">
            {Object.keys(stockDraft).length} × {t('admin.stock')}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStockDraft({})}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saveStock.isPending} onClick={() => saveStock.mutate()} >
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : null}

      <Panel padded={false}>
        {products.isPending ? (
          <Spinner label={t('common.loading')} />
        ) : products.isError ? (
          <ErrorBox message={(products.error as Error).message} onRetry={() => void products.refetch()} />
        ) : products.data.items.length === 0 ? (
          <Empty
            title={t('storefront.noProducts')}
            action={
              <Button onClick={() => setEditing('new')}>
                {t('admin.addProduct')}
              </Button>
            }
          />
        ) : (
          <Rows>
            {products.data.items.map((product) => (
              <div key={product.id} style={{ borderBottom: '1px solid var(--color-line)' }}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <div
                    className="size-11 shrink-0 overflow-hidden rounded-lg"
                    style={{ background: 'var(--color-canvas)' }}
                  >
                    {product.images[0] ? (
                      <img src={product.images[0].url} alt="" className="size-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{pickText(product.name, locale)}</p>
                    <div className="flex items-center gap-2">
                      <Money
                        amount={product.price.amount}
                        currency={product.price.currency}
                        className="text-xs tabular-nums"
                      />
                      <Pill tone={product.status === 'active' ? 'positive' : 'neutral'}>
                        {product.status}
                      </Pill>
                      {product.totalStock !== null && product.totalStock <= 3 ? (
                        <Pill tone={product.totalStock === 0 ? 'danger' : 'warn'}>
                          {product.totalStock}
                        </Pill>
                      ) : null}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setEditing(product)}>
                    {t('common.edit')}
                  </Button>
                </div>

                {product.variants.length > 0 ? (
                  <div className="flex flex-wrap gap-2 px-4 pb-3">
                    {[...product.variants]
                      .sort((a, b) => compareSizes(a.size ?? '', b.size ?? ''))
                      .map((variant) => (
                        <label
                          key={variant.id}
                          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs"
                          style={{ border: '1px solid var(--color-line)' }}
                        >
                          <span style={{ color: 'var(--color-ink-soft)' }}>
                            {[variant.color, variant.size].filter(Boolean).join('/') || '—'}
                          </span>
                          <input
                            type="number"
                            min={0}
                            className="w-14 rounded border-none bg-transparent text-right tabular-nums"
                            value={stockDraft[variant.id] ?? variant.stock}
                            onChange={(event) =>
                              setStockDraft((draft) => ({
                                ...draft,
                                [variant.id]: Math.max(0, Number(event.target.value) || 0),
                              }))
                            }
                          />
                        </label>
                      ))}
                  </div>
                ) : null}
              </div>
            ))}
          </Rows>
        )}
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Product form                                                        */
/* ------------------------------------------------------------------ */

function ProductForm({
  product,
  onDone,
  onCancel,
}: {
  product: ProductDto | null;
  onDone(): void;
  onCancel(): void;
}) {
  const { shopId, t } = useSession();
  /**
   * §4: the vertical decides which extension panel this form shows — variants
   * for retail, modifier groups for F&B, duration and deposit for services.
   * They are alternatives, never siblings.
   */
  const shopQuery = useQuery({
    queryKey: ['shop', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.shop(shopId!),
    staleTime: 5 * 60_000,
  });
  const vertical = shopQuery.data?.shop.vertical ?? 'retail';
  const [nameKm, setNameKm] = useState(product?.name.km ?? '');
  const [nameEn, setNameEn] = useState(product?.name.en ?? '');
  const [descKm, setDescKm] = useState(product?.description.km ?? '');
  const [price, setPrice] = useState(
    product ? String(toDecimal(product.price)) : '',
  );
  const [compareAt, setCompareAt] = useState(
    product?.compareAtPrice ? String(toDecimal(product.compareAtPrice)) : '',
  );
  const [imageUrl, setImageUrl] = useState(product?.images[0]?.url ?? '');
  const [status, setStatus] = useState<'draft' | 'active'>(
    product?.status === 'active' ? 'active' : 'draft',
  );
  const [sizesText, setSizesText] = useState(
    product ? [...new Set(product.variants.map((v) => v.size).filter(Boolean))].join(', ') : 'S, M, L',
  );
  const [colorsText, setColorsText] = useState(
    product ? [...new Set(product.variants.map((v) => v.color).filter(Boolean))].join(', ') : '',
  );
  const [defaultStock, setDefaultStock] = useState('5');
  const [error, setError] = useState<string | null>(null);

  const currency = product?.price.currency ?? 'USD';

  const save = useMutation({
    mutationFn: () => {
      const priceAmount = parseAmount(price || '0', currency).amount;
      if (priceAmount <= 0) throw new ApiError('validation_failed', t('errors.validation_failed'));

      const sizes = sizesText.split(',').map((s) => s.trim()).filter(Boolean);
      const colors = colorsText.split(',').map((s) => s.trim()).filter(Boolean);

      /**
       * Editing keeps the existing variant rows so their ids — and therefore
       * their stock and their links from past orders — survive. Only a brand
       * new product generates a fresh grid.
       */
      const variants: UpsertProductInput['variants'] = product
        ? product.variants.map((variant) => ({
            id: variant.id,
            sku: variant.sku,
            size: variant.size,
            color: variant.color,
            colorHex: variant.colorHex,
            priceAmount: variant.price?.amount ?? null,
            stock: variant.stock,
            imageUrl: variant.imageUrl,
            isActive: variant.isActive,
          }))
        : buildGrid(sizes, colors, Number(defaultStock) || 0);

      const body: UpsertProductInput = {
        name: { km: nameKm, en: nameEn },
        description: { km: descKm, en: '' },
        priceAmount,
        compareAtPriceAmount: compareAt ? parseAmount(compareAt, currency).amount : null,
        images: imageUrl ? [{ url: imageUrl }] : [],
        status,
        trackInventory: true,
        variants,
      };

      return product
        ? api.updateProduct(shopId!, product.id, body)
        : api.createProduct(shopId!, body);
    },
    onSuccess: onDone,
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={onCancel}>
          ‹ {t('common.back')}
        </Button>
        <h1 className="text-base font-semibold">
          {product ? t('common.edit') : t('admin.addProduct')}
        </h1>
        <Button className="ml-auto" disabled={save.isPending || nameKm.trim() === '' || price.trim() === ''} onClick={() => save.mutate()} >
          {save.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </div>

      {error ? <ErrorBox message={error} /> : null}

      <Panel title={t('admin.products')}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`${t('checkout.name')} (ខ្មែរ)`}>
            <Input value={nameKm} onChange={(e) => setNameKm(e.target.value)} />
          </Field>
          <Field label={`${t('checkout.name')} (EN)`} hint={t('common.optional')}>
            <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
          </Field>
          <Field label={t('common.price')} hint={currency}>
            <Input
 
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </Field>
          <Field label="Compare-at" hint={t('common.optional')}>
            <Input
 
              inputMode="decimal"
              value={compareAt}
              onChange={(e) => setCompareAt(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-3">
          <Field label="Image URL" hint={t('common.optional')}>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
          </Field>
        </div>

        <div className="mt-3">
          <Field label={t('checkout.note')}>
            <Textarea
 
              rows={3}
              value={descKm}
              onChange={(e) => setDescKm(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={status === 'active'}
              onChange={(e) => setStatus(e.target.checked ? 'active' : 'draft')}
            />
            {t('admin.publish')}
          </label>
        </div>
      </Panel>

      {vertical === 'retail' && !product ? (
        <Panel title={t('admin.variants')}>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={t('common.size')} hint="S, M, L, XL">
              <Input value={sizesText} onChange={(e) => setSizesText(e.target.value)} />
            </Field>
            <Field label={t('common.color')} hint="Black, Cream">
              <Input value={colorsText} onChange={(e) => setColorsText(e.target.value)} />
            </Field>
            <Field label={t('admin.stock')}>
              <Input
 
                type="number"
                min={0}
                value={defaultStock}
                onChange={(e) => setDefaultStock(e.target.value)}
              />
            </Field>
          </div>
          <p className="mt-2 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
            {gridSize(sizesText, colorsText)} × {t('admin.variants')}
          </p>
        </Panel>
      ) : vertical === 'retail' && product ? (
        <Panel title={t('admin.variants')} padded={false}>
          <Rows>
            {product.variants.map((variant) => (
              <Row key={variant.id}>
                <span className="flex-1 text-sm">
                  {[variant.color, variant.size].filter(Boolean).join(' / ') || '—'}
                </span>
                <span className="text-xs tabular-nums" style={{ color: 'var(--color-ink-soft)' }}>
                  {variant.sku}
                </span>
                <Pill tone={variant.stock > 0 ? 'neutral' : 'danger'}>{variant.stock}</Pill>
              </Row>
            ))}
          </Rows>
          <p className="p-4 text-xs" style={{ color: 'var(--color-ink-soft)' }}>
            {t('admin.stock')} — {t('common.edit')}: {t('admin.products')}
          </p>
        </Panel>
      ) : null}

      {/*
        The vertical's own half of the product (§4). Only once the product
        exists: its extension row hangs off a product id, so there is nothing
        to attach a modifier group or a duration to before then.
      */}
      {product && vertical !== 'retail' ? (
        <ProductVerticalPanel vertical={vertical} product={product} />
      ) : null}
    </div>
  );
}

function splitList(value: string): string[] {
  return value.split(',').map((entry) => entry.trim()).filter(Boolean);
}

function gridSize(sizesText: string, colorsText: string): number {
  const sizes = splitList(sizesText).length || 1;
  const colors = splitList(colorsText).length || 1;
  return sizes * colors;
}

/** Local mirror of the server's matrix builder, so the form previews honestly. */
function buildGrid(sizes: string[], colors: string[], stock: number): UpsertProductInput['variants'] {
  const sizeAxis = sizes.length > 0 ? sizes : [null];
  const colorAxis = colors.length > 0 ? colors : [null];
  const variants: UpsertProductInput['variants'] = [];
  for (const color of colorAxis) {
    for (const size of sizeAxis) {
      variants.push({ size, color, stock, isActive: true });
    }
  }
  return variants;
}
